import type { DesktopUpdates } from '@repo/desktop-bridge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { useEffect, useState } from 'react';

/** Calls `tick` on a schedule and returns how to stop. Injected so a test fires it by hand. */
export type Every = (tick: () => void) => () => void;

const POLL_MS = 60 * 1000;

const everyMinute: Every = (tick) => {
  const timer = setInterval(tick, POLL_MS);
  return () => {
    clearInterval(timer);
  };
};

/**
 * "Update received", once per version for the life of the window (#310).
 *
 * **Information only, with no Restart.** The basket being rung up and a parked
 * one live only in React state, so a restart mid-sale loses them; the update
 * installs when the till is next closed (`autoInstallOnAppQuit`), as it always
 * has. Polls `waiting()` rather than listening for a push because the preload
 * pushes nothing, and a dialog a minute late costs nobody anything.
 *
 * `null` updates — the back office, or a shell older than #288 — draws nothing.
 */
export function UpdateReceived({
  updates,
  every = everyMinute,
}: {
  updates: DesktopUpdates | null;
  every?: Every;
}) {
  const [waiting, setWaiting] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    if (updates === null) return;
    let live = true;

    // A failed read is no dialog, never a message: an update is not the
    // counter's problem.
    const read = () => {
      updates
        .waiting()
        .then((next) => {
          if (live) setWaiting(next);
        })
        .catch(() => undefined);
    };

    read();
    const stop = every(read);

    return () => {
      live = false;
      stop();
    };
  }, [updates, every]);

  if (waiting === null) return null;

  return (
    <Dialog
      open={waiting !== dismissed}
      onOpenChange={(open) => {
        if (!open) setDismissed(waiting);
      }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Update received</DialogTitle>
          <DialogDescription>
            Version {waiting} is ready. It installs the next time the till is closed. To start using
            it now, finish the sale in progress, then close the till and open it again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            size="touch"
            onClick={() => {
              setDismissed(waiting);
            }}
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
